import { useState } from 'react';
import type { MessageItem } from '@wallflower/shared';
import { MAX_AUTHOR_LENGTH, MAX_MESSAGE_LENGTH } from '@wallflower/shared';

/** Remembered so the host types their name once, not once per announcement. */
const HOST_NAME_KEY = 'wallflower:host-name';

interface Props {
  messages: MessageItem[];
  allowMessages: boolean;
  onPost: (body: string, authorName: string | null) => Promise<void>;
  onToggleHidden: (message: MessageItem) => void;
  onDelete: (message: MessageItem) => void;
}

export default function MessageList({
  messages,
  allowMessages,
  onPost,
  onToggleHidden,
  onDelete,
}: Props) {
  const [draft, setDraft] = useState('');
  const [authorName, setAuthorName] = useState(
    () => localStorage.getItem(HOST_NAME_KEY) ?? '',
  );
  const [posting, setPosting] = useState(false);
  const hiddenCount = messages.filter((m) => m.hidden).length;

  async function post() {
    const text = draft.trim();
    if (!text) return;
    const trimmedName = authorName.trim();
    localStorage.setItem(HOST_NAME_KEY, trimmedName);
    setPosting(true);
    try {
      await onPost(text, trimmedName || null);
      setDraft('');
    } finally {
      setPosting(false);
    }
  }

  return (
    <section className="panel p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 className="eyebrow">The message ticker</h2>
        <p className="text-xs text-muted">
          {messages.length} message{messages.length === 1 ? '' : 's'}
          {hiddenCount > 0 ? ` · ${hiddenCount} hidden` : ''}
        </p>
      </div>

      <div className="mt-4">
        <textarea
          className="field resize-none"
          rows={2}
          value={draft}
          onChange={(e) => setDraft(e.target.value.slice(0, MAX_MESSAGE_LENGTH))}
          placeholder="Announce something - cake at nine, taxis at midnight"
          maxLength={MAX_MESSAGE_LENGTH}
          aria-label="Post an announcement"
        />
        <input
          className="field mt-2"
          value={authorName}
          onChange={(e) => setAuthorName(e.target.value)}
          placeholder="Your hosts"
          maxLength={MAX_AUTHOR_LENGTH}
          aria-label="Name shown on your announcements (optional)"
        />
        <div className="mt-2 flex items-center gap-3">
          <button
            className="btn btn-primary px-4 py-2 text-sm"
            onClick={() => void post()}
            disabled={posting || !draft.trim()}
          >
            {posting ? 'Posting…' : 'Post as host'}
          </button>
          <span className="text-xs text-muted">{MAX_MESSAGE_LENGTH - draft.length} left</span>
        </div>
      </div>

      {!allowMessages ? (
        <p className="mt-4 text-xs text-sulphur">
          Guests can't add messages right now. Yours still show.
        </p>
      ) : null}

      {messages.length === 0 ? (
        <p className="mt-6 mb-2 text-center text-sm text-muted">
          No messages yet. Guests can send one from the same page they upload photos.
        </p>
      ) : (
        <ul className="mt-5 grid gap-2">
          {messages.map((message) => (
            <li
              key={message.id}
              className="rounded-lg border border-edge p-3"
              style={{ opacity: message.hidden ? 0.45 : 1 }}
            >
              <div className="flex items-baseline justify-between gap-2">
                <span
                  className="font-display text-sm font-semibold"
                  style={{
                    color: message.fromHost ? 'var(--color-sulphur)' : 'var(--color-petal)',
                  }}
                >
                  {message.authorName ?? (message.fromHost ? 'Your hosts' : 'Someone')}
                  {message.fromHost ? (
                    <span className="ml-2 text-[10px] tracking-wider text-muted uppercase">
                      Host
                    </span>
                  ) : null}
                </span>
                <span className="shrink-0 text-xs text-muted">
                  {new Date(message.createdAt).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
              </div>

              <p className="mt-1 text-sm break-words text-chalk/90">{message.body}</p>

              <div className="mt-3 flex gap-3 text-xs">
                <button className="text-petal hover:underline" onClick={() => onToggleHidden(message)}>
                  {message.hidden ? 'Put back on screen' : 'Hide from screen'}
                </button>
                <DeleteButton onConfirm={() => onDelete(message)} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** Two taps to delete, matching the media lightbox. */
function DeleteButton({ onConfirm }: { onConfirm: () => void }) {
  const [confirming, setConfirming] = useState(false);

  return (
    <button
      className="ml-auto hover:underline"
      style={{ color: 'var(--color-danger)' }}
      onClick={() => (confirming ? onConfirm() : setConfirming(true))}
      onBlur={() => setConfirming(false)}
    >
      {confirming ? 'Tap again to delete' : 'Delete'}
    </button>
  );
}
